import { useState } from "react";
import { ChevronDown, Columns3, Merge, Package, Trash2, X } from "lucide-react";

import { CSICodePicker } from "@/components/csi-code-picker";
import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";
import {
  SubmittalStatusDropdown,
  SubmittalTypeDropdown,
} from "@/components/submittal/column-dropdowns";
import type { TrackerType } from "@/components/tracker-view-modal";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { CustomTrackerDialog } from "./custom-tracker-dialog";
import type { CustomTrackerInfo, TrackerCounts } from "./tracker-config";
import { TrackerMenuItems } from "./tracker-menu-items";

interface SubmittalSelectionToolbarProps {
  selectedRowCount: number;
  headers: string[];
  csiCodeCol: number;
  submittalStatusCol?: number;
  submittalTypeCol?: number;
  trackerCounts: TrackerCounts;
  customTrackerNames: CustomTrackerInfo[];
  onBulkCSIAssign: (code: string, title: string, divisionCode?: string, divisionTitle?: string) => void;
  onBulkColumnAssign: (colIndex: number, value: string, columnName: string) => void;
  onAddToTracker: (type: TrackerType, customTrackerName?: string) => void;
  onCreateCustomTracker: (name: string) => void | Promise<void>;
  onDeleteRows: () => void | Promise<void>;
  onMergeRows: () => void;
  onClearSelection: () => void;
  className?: string;
}

function SetColumnPopover({
  headers,
  onApply,
}: {
  headers: string[];
  onApply: (colIndex: number, value: string, columnName: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [columnIndex, setColumnIndex] = useState("");
  const [value, setValue] = useState("");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="compact">
          <Columns3 />
          Set Column
          <ChevronDown className="size-3 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-3 p-3">
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (columnIndex === "") return;
            const index = Number(columnIndex);
            onApply(index, value, headers[index] || `Column ${index + 1}`);
            setOpen(false);
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="bulk-set-column">Column</Label>
            <Select value={columnIndex} onValueChange={setColumnIndex}>
              <SelectTrigger id="bulk-set-column" className="h-8 w-full text-sm">
                <SelectValue placeholder="Choose a column" />
              </SelectTrigger>
              <SelectContent className="z-[160]">
                {headers.map((header, index) => (
                  <SelectItem key={`${header}-${index}`} value={String(index)}>
                    {header || `Column ${index + 1}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bulk-set-value">Value</Label>
            <Input
              id="bulk-set-value"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" size="compact" disabled={columnIndex === ""}>
              Apply
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/** Bulk actions for the selected grid rows. Render only while at least one row is selected. */
export function SubmittalSelectionToolbar({
  selectedRowCount,
  headers,
  csiCodeCol,
  submittalStatusCol,
  submittalTypeCol,
  trackerCounts,
  customTrackerNames,
  onBulkCSIAssign,
  onBulkColumnAssign,
  onAddToTracker,
  onCreateCustomTracker,
  onDeleteRows,
  onMergeRows,
  onClearSelection,
  className,
}: SubmittalSelectionToolbarProps) {
  const [isCreateTrackerDialogOpen, setIsCreateTrackerDialogOpen] = useState(false);
  const rowsLabel = `${selectedRowCount} row${selectedRowCount !== 1 ? "s" : ""}`;

  return (
    <div
      role="toolbar"
      aria-label="Selected rows"
      className={cn(
        "flex min-h-9 min-w-0 flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary/5 py-1 ps-3 pe-1",
        className
      )}
    >
      <span className="text-sm font-semibold tabular-nums text-primary" aria-live="polite">
        {selectedRowCount} selected
      </span>
      <span className="h-4 w-px bg-border" aria-hidden="true" />

      {csiCodeCol >= 0 ? (
        <CSICodePicker onSelect={onBulkCSIAssign} className="h-7 text-sm" />
      ) : null}
      {submittalStatusCol !== undefined && submittalStatusCol >= 0 ? (
        <SubmittalStatusDropdown
          placeholder="Set Status"
          onChange={(value) => onBulkColumnAssign(submittalStatusCol, value, "Status")}
          className="h-7 w-[130px] text-sm"
        />
      ) : null}
      {submittalTypeCol !== undefined && submittalTypeCol >= 0 ? (
        <SubmittalTypeDropdown
          placeholder="Set Type"
          onChange={(value) => onBulkColumnAssign(submittalTypeCol, value, "Type")}
          className="h-7 w-[130px] text-sm"
        />
      ) : null}
      <SetColumnPopover headers={headers} onApply={onBulkColumnAssign} />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="compact">
            <Package />
            Add to Tracker
            <ChevronDown className="size-3 opacity-50" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <TrackerMenuItems
            trackerCounts={trackerCounts}
            customTrackers={customTrackerNames}
            onAddToTracker={onAddToTracker}
            onCreateCustomTracker={() => setIsCreateTrackerDialogOpen(true)}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      {selectedRowCount >= 2 ? (
        <Button variant="outline" size="compact" onClick={onMergeRows}>
          <Merge />
          Merge
        </Button>
      ) : null}
      <DeleteConfirmationDialog
        title="Delete selected rows?"
        description={`This will delete ${rowsLabel} from the submittal.`}
        onConfirm={onDeleteRows}
        confirmLabel="Delete"
      >
        <Button variant="destructive" size="compact">
          <Trash2 />
          Delete
        </Button>
      </DeleteConfirmationDialog>

      <IconButton
        label="Clear selection"
        icon={X}
        size="compact"
        className="ms-auto"
        onClick={onClearSelection}
      />

      <CustomTrackerDialog
        open={isCreateTrackerDialogOpen}
        onOpenChange={setIsCreateTrackerDialogOpen}
        onCreateTracker={onCreateCustomTracker}
      />
    </div>
  );
}
